import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { isFragment } from 'react-is';

import classNames from 'classnames';

import styles from './Modal.module.css';

import { Close } from '../../icons/Close';
import { ButtonBase } from '../ButtonBase';
import type { LightboxProps } from '../Lightbox';
import { Lightbox } from '../Lightbox';
import { Text } from '../Text';
import { Wave } from '../Wave';

/** Variants for the Modal component. */
export enum ModalVariant {
  /** The modal header with a white background. */
  Default = 'default',
  /** The modal header with a wave background. */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Wave = 'wave',
}

export interface ModalProps extends LightboxProps {
  /**
   * The width of the modal.
   *
   * @defaultValue '' (the modal will be as wide as its content)
   */
  width?: LightboxProps['width'];
  /**
   * The height of the modal.
   *
   * @defaultValue 0 (the modal will be as tall as its content)
   */
  height?: LightboxProps['height'];
  /**
   * Display a close button in the top right corner.
   *
   * @defaultValue false
   */
  withCloseButton?: boolean;
  /**
   * ARIA label for the close button.
   *
   * @defaultValue 'Close'
   */
  closeButtonLabel?: string;
  /**
   * Optional handler for changing external managed state when a modal close is
   * requested (e.g close button, escape key, backdrop click). If this property
   * is present, the `onClose` handler will not be called explicitly when the
   * close button is clicked, but only in response to the `isOpen` prop
   * changing.
   */
  onCloseRequested?: () => void;
  /**
   * The variant of the modal.
   *
   * @defaultValue ModalVariant.Default
   */
  variant?: ModalVariant;
  /**
   * The heading of the modal. This is used as ARIA label for the dialog
   * element, if not provided.
   */
  heading?: string | ReactNode;
  /**
   * The sub heading of the modal. This is used as ARIA description for the
   * dialog element,, if not provided.
   */
  subHeading?: string | ReactNode;
}

/**
 * Render a modal dialog with appropriate headings and an optional close button.
 * Inherits props and behavior from {@link Lightbox}.
 *
 * @cssVariable --tfd-modal-border-radius The border radius of the modal corners
 * @cssVariable --tfd-modal-padding The padding around the modal content
 * @cssVariable --tfd-modal-max-width The maximum width of the modal, responsive at different breakpoints
 * @see https://corgi-x.tfd.engineering/components/modal
 */
export function Modal(props: ModalProps) {
  const [isOpen, setIsOpen] = useState(props.isOpen);
  const headingId = useId();
  const subHeadingId = useId();

  const {
    width = '',
    height = 0,
    variant = ModalVariant.Default,
    heading,
    subHeading,
    withCloseButton,
    onCloseRequested,
    ...lightboxProps
  } = props;

  // Toggle the open status from props.
  useEffect(() => {
    if (props.isOpen === isOpen) {
      return;
    }
    setIsOpen(props.isOpen);
  }, [props.isOpen, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    props.onClose?.();
  };

  const fullHeader = (
    <div>
      {typeof heading === 'string' || isFragment(heading) ? (
        <Text
          id={headingId}
          as="h1"
          variant="heading-40"
          color="Kale3"
          bold
          bottomSpacing={!subHeading ? 'lg' : undefined}
        >
          {heading}
        </Text>
      ) : (
        heading && <div id={headingId}>{heading}</div>
      )}
      {typeof subHeading === 'string' || isFragment(subHeading) ? (
        <Text id={subHeadingId} as="h2" variant="heading-22">
          {subHeading}
        </Text>
      ) : (
        subHeading && <div id={subHeadingId}>{subHeading}</div>
      )}
    </div>
  );

  const navBar = (
    <nav className={styles.nav}>
      <ButtonBase
        className={styles.closeButton}
        type="button"
        onClick={onCloseRequested ?? handleClose}
        aria-label={props.closeButtonLabel ?? 'Close'}
      >
        <Close aria-hidden baseColor="Kale3" height={36} />
      </ButtonBase>
    </nav>
  );

  // Use the heading and the subheading for label and description, if not provided
  const labelledBy = !props['aria-label'] && heading ? headingId : undefined;
  const describedBy =
    !props['aria-description'] && subHeading ? subHeadingId : undefined;

  return (
    <Lightbox
      width={width}
      height={height}
      isOpen={isOpen}
      onClose={onCloseRequested ? undefined : handleClose}
      onCloseRequested={onCloseRequested}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      {...lightboxProps}
      contentClassName={classNames(
        styles.modal,
        lightboxProps.contentClassName,
        [styles[`variant-${variant}`]]
      )}
    >
      {variant !== ModalVariant.Wave && withCloseButton ? navBar : null}
      {variant === ModalVariant.Wave ? (
        <Wave withBottom fill="Chickpea1" className={styles.wave}>
          {withCloseButton ? navBar : null}
          {fullHeader}
        </Wave>
      ) : (
        fullHeader
      )}
      {props.children && <div className={styles.content}>{props.children}</div>}
    </Lightbox>
  );
}
